// JsSIP SIP-over-WebRTC client
// Connects directly to FreePBX — no backend relay for audio.

import JsSIP from 'jssip'
import toast from 'react-hot-toast'
import { useCallStore } from '../store/call'
import { useSipStore } from '../store/sip'
import api from '../api/client'

interface SipConfig {
  sipUsername: string
  sipPassword: string
  pbxWsUri: string
  pbxHost: string
  displayName?: string | null
}

let sipConfig: SipConfig | null = null
let ua: JsSIP.UA | null = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let currentSession: any = null
let registered = false
// Incremented each time we create a new UA; handlers from old UAs check this and self-skip
let uaGeneration = 0

// Pending call — queued while registration is in progress
let pendingCallFn: (() => void) | null = null
let pendingCallTimer: ReturnType<typeof setTimeout> | null = null

function clearPendingCall() {
  pendingCallFn = null
  if (pendingCallTimer) { clearTimeout(pendingCallTimer); pendingCallTimer = null }
}

function getUA(): JsSIP.UA {
  if (ua) return ua

  // Silence JsSIP debug logs in production
  if (import.meta.env.DEV) {
    JsSIP.debug.enable('JsSIP:*')
  } else {
    JsSIP.debug.disable()
  }

  const socket = new JsSIP.WebSocketInterface(sipConfig!.pbxWsUri)

  console.info('[SIP] Initialising UA — uri:', `sip:${sipConfig!.sipUsername}@${sipConfig!.pbxHost}`, '| ws:', sipConfig!.pbxWsUri)

  const myGen = ++uaGeneration

  ua = new JsSIP.UA({
    sockets: [socket],
    uri: `sip:${sipConfig!.sipUsername}@${sipConfig!.pbxHost}`,
    password: sipConfig!.sipPassword,
    display_name: sipConfig!.displayName ?? sipConfig!.sipUsername,
    register: true,
    register_expires: 300,
    session_timers: false,
    connection_recovery_min_interval: 2,
    connection_recovery_max_interval: 30,
  })

  ua.on('registered', () => {
    if (uaGeneration !== myGen) return
    registered = true
    useSipStore.getState()._setStatus('registered')
    console.info('[SIP] Registered as', sipConfig?.sipUsername)
    // Fire any call that was queued before registration completed
    if (pendingCallFn) {
      const fn = pendingCallFn
      clearPendingCall()
      fn()
    }
  })

  ua.on('unregistered', () => {
    if (uaGeneration !== myGen) return
    registered = false
    useSipStore.getState()._setStatus('disconnected')
    console.info('[SIP] Unregistered')
  })

  ua.on('registrationFailed', (e: any) => {
    if (uaGeneration !== myGen) return
    registered = false
    useSipStore.getState()._setStatus('disconnected')
    console.error('[SIP] Registration failed:', e.cause)
    if (pendingCallFn) {
      clearPendingCall()
      const store = useCallStore.getState()
      store._setCallState('ended')
      toast.error('Call failed — SIP registration error. Check your call configuration.')
      setTimeout(() => store.reset(), 3000)
    }
  })

  ua.on('disconnected', () => {
    if (uaGeneration !== myGen) return
    registered = false
    useSipStore.getState()._setStatus('disconnected')
    // Don't wait for JsSIP's exponential backoff (up to 30 s) — recreate
    // the UA immediately so the next call doesn't sit pending for ~45 s.
    setTimeout(() => { if (uaGeneration === myGen && sipConfig) retrySip() }, 1500)
  })

  ua.start()
  return ua
}

/** Call once on app boot to pre-register so the first call is instant */
export function initSip(config: SipConfig) {
  sipConfig = config
  useSipStore.getState()._setHasCredentials(true)
  // If a UA is already running (e.g. called twice on session restore), leave it alone —
  // resetting status here would overwrite a 'registered' state that is still valid.
  if (ua) return
  useSipStore.getState()._setStatus('connecting')
  getUA()
}

/** Stop the UA and clear all state — call on logout */
export function teardownSip() {
  if (ua) {
    try { ua.stop() } catch { /* ignore */ }
    ua = null
  }
  registered = false
  currentSession = null
  sipConfig = null
  const sipStore = useSipStore.getState()
  sipStore._setStatus('no_config')
  sipStore._setHasCredentials(false)
}

/** Tear down and restart the UA — use when registration has failed/dropped */
export function retrySip() {
  if (!sipConfig) return
  // Bump generation BEFORE stopping so the old UA's async 'disconnected' event
  // is ignored by its handler (generation mismatch check).
  uaGeneration++
  if (ua) {
    try { ua.stop() } catch { /* ignore */ }
    ua = null
    registered = false
  }
  useSipStore.getState()._setStatus('connecting')
  getUA()
}

export function isSipRegistered() {
  return registered
}

export function decodeRt(v: string): string {
  const k = [79, 58, 124, 33, 94, 157, 43, 129, 99, 71, 14, 242, 56, 90, 180, 125]
  const raw = atob(v)
  return Array.from(raw).map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ k[i % k.length])).join('')
}

export function startCall(
  phone: string,
  leadName: string,
  companyId: string,
  refId: string,
) {
  const store = useCallStore.getState()

  // Prevent starting a second call while one is already active or pending
  if (store.state !== 'idle') return

  // Guard: no call config loaded
  if (!sipConfig) {
    console.warn('[SIP] No call config — cannot place call')
    return
  }

  store._setLead(leadName, phone, companyId, refId)
  store._setCallState('connecting')

  const ua = getUA()

  const doCall = () => {
    const target = `sip:${phone}@${sipConfig!.pbxHost}`

    const sessionHandlers = {
      progress: (_e: any) => {
        store._setCallState('ringing')
      },

      confirmed: (_e: any) => {
        store._setCallState('in_call')
      },

      ended: (_e: any) => {
        const dur = useCallStore.getState().durationSeconds
        store._setCallState('ended')
        postCallLog(companyId, refId, phone, dur, 'completed', sipConfig!.sipUsername, providerCallId)
        setTimeout(() => store.reset(), 3000)
        currentSession = null
      },

      failed: (e: any) => {
        console.warn('[SIP] Call failed:', e.cause)
        const dur = useCallStore.getState().durationSeconds
        store._setCallState('ended')
        const status = e.cause === 'Rejected' ? 'rejected' : e.cause === 'Busy' ? 'busy' : 'failed'
        postCallLog(companyId, refId, phone, dur, status, sipConfig!.sipUsername, providerCallId)
        setTimeout(() => store.reset(), 3000)
        currentSession = null
      },

      // Wire remote audio as soon as we have a peer connection
      peerconnection: (e: any) => {
        const pc: RTCPeerConnection = e.peerconnection
        pc.ontrack = (ev: RTCTrackEvent) => {
          if (ev.streams && ev.streams[0]) {
            const audio = document.getElementById('pbx-audio-out') as HTMLAudioElement | null
            if (audio) {
              audio.srcObject = ev.streams[0]
              audio.play().catch(() => {/* autoplay policy — user gesture already happened */})
            }
          }
        }
      },
    }

    let providerCallId = ''
    try {
      currentSession = ua.call(target, {
        eventHandlers: sessionHandlers,
        mediaConstraints: { audio: true, video: false },
        rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 0 },
        pcConfig: {
          iceServers: [],
        },
      } as any)
      providerCallId = (currentSession as any)?.id ?? ''
    } catch (err) {
      console.error('[SIP] ua.call() threw:', err)
      store._setCallState('ended')
      setTimeout(() => store.reset(), 3000)
    }
  }

  if (registered) {
    // Already registered — dial immediately
    doCall()
  } else {
    // Queue the call; it will fire as soon as registration completes
    console.info('[SIP] Not yet registered — queuing call for', phone)
    clearPendingCall()
    pendingCallFn = doCall
    // Safety timeout: if registration never arrives within 45s, fail gracefully
    pendingCallTimer = setTimeout(() => {
      if (pendingCallFn) {
        clearPendingCall()
        console.warn('[SIP] Registration timeout — aborting queued call')
        store._setCallState('ended')
        toast.error('Call failed — could not connect to PBX. Try again later.')
        setTimeout(() => store.reset(), 3000)
      }
    }, 45_000)
  }
}

export function hangup() {
  clearPendingCall()
  if (currentSession) {
    try { currentSession.terminate() } catch { /* already terminated */ }
  } else {
    // No active session (e.g. stuck in connecting/queued) — reset the store directly
    const store = useCallStore.getState()
    if (store.state !== 'idle') {
      store._setCallState('ended')
      setTimeout(() => store.reset(), 1500)
    }
  }
}

export function toggleMute() {
  const store = useCallStore.getState()
  if (!currentSession) return
  if (store.isMuted) {
    currentSession.unmute({ audio: true })
  } else {
    currentSession.mute({ audio: true })
  }
  store.toggleMute()
}

// ── Call log ─────────────────────────────────────────────────────────────────

async function postCallLog(
  companyId: string,
  refId: string,
  toNumber: string,
  duration: number,
  status: 'completed' | 'no_answer' | 'failed' | 'busy' | 'rejected',
  fromNumber?: string,
  providerCallId?: string,
) {
  try {
    await api.post(`/companies/${companyId}/leads/${refId}/calls/log`, {
      toNumber,
      duration,
      status,
      ...(fromNumber ? { fromNumber } : {}),
      ...(providerCallId ? { providerCallId } : {}),
    })
  } catch (err) {
    console.warn('[SIP] Failed to post call log', err)
  }
}
